import React, { useState, useRef, useEffect } from 'react';

import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import LockerInfoEdit from '../../../Component/LockerComponent/LockerInfoEdit';
import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable, closestCenter } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import { ReactComponent as PlusSvg } from '../../../image/Plus.svg';
import { ReactComponent as DeleteSvg } from '../../../image/Delete.svg';

// 락커 셀 — useDraggable + useDroppable (드래그 소스이자 드롭 타겟) //
function DraggableLockerCell(props) {
  const { cellKey, isDraggable, isDragOver, children } = props;
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({ id: cellKey, disabled: !isDraggable });
  const { setNodeRef: setDropRef } = useDroppable({ id: cellKey, disabled: !isDraggable });

  function setRefs(el) {
    setDragRef(el);
    setDropRef(el);
  }

  // 드래그 중인 본인은 원래 위치에 0.4 잔상으로 고정 (DragOverlay가 따라다님) //
  const style = {
    transform: isDragging ? undefined : CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1
  };

  return (
    <DragWrapper
      ref={setRefs}
      style={style}
      data-cell-key={cellKey}
      $isDraggable={isDraggable}
      $isDragging={isDragging}
      $isDragOver={isDragOver}
      {...attributes}
      {...listeners}>
      {children}
    </DragWrapper>
  );
}

const FALLBACK_COLS = 7;

function LockerEditView(props) {
  const {
    data,
    divisionIndex,
    divisionNameDraft,
    LockerRef,
    onLockChange,
    onNameChange,
    onClickUsageHistoryFn,
    onClickLockerActionFn,
    onClickChangeStateFn,
    onClickDeleteLockerFn,
    onClickChangeNameFn,
    onClickSendMessageFn,
    onClickExtendFn,
    onAddLockersChange,
    onDeleteLockersFn,
    onSwapChange,
    excludedLockerIds = []
  } = props;

  const { t } = useLanguage();
  const bodyRef = useRef(null);

  const [addedCols, setAddedCols] = useState(0);
  const [addedRows, setAddedRows] = useState(0);
  const [swapMap, setSwapMap] = useState({});
  const [dragSourceKey, setDragSourceKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [newLockerPosMap, setNewLockerPosMap] = useState(null);

  // 신규 락커의 최초 좌표 (이름 고정용) //
  const newLockerInitialPositions = useRef({});

  // dnd-kit sensors — TouchSensor는 iOS long-press(250ms) 활성화 //
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const excludedIdSet = new Set(excludedLockerIds);
  const visibleLockers = data.lockers.filter(l => !excludedIdSet.has(l.id));

  const hasDuplicateCoords = (() => {
    const seen = new Set();
    return visibleLockers.some(l => {
      const key = `${l.x ?? 0},${l.y ?? 0}`;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
  })();

  // 좌표 중복이 있거나 삭제가 있을 때만 collapse, 그 외에는 절대 좌표 유지 (swap 위치 존중) //
  const lockerMap = {};
  let maxX = -1;
  let maxY = -1;

  // base grid 크기는 existing 락커의 원래 좌표 기준으로 고정 (swap 영향 X) //
  let baseMaxX = -1;
  let baseMaxY = -1;

  const visiblePositions = [];
  data.lockers.forEach((l, i) => {
    if (excludedIdSet.has(l.id)) return;
    const defaultPos = hasDuplicateCoords
      ? { x: i % FALLBACK_COLS, y: Math.floor(i / FALLBACK_COLS) }
      : { x: l.x ?? 0, y: l.y ?? 0 };
    const pos = (l.id && swapMap[l.id]) ? swapMap[l.id] : defaultPos;
    visiblePositions.push({ locker: l, arrayIdx: i, origX: pos.x, origY: pos.y });

    if (defaultPos.x > baseMaxX) baseMaxX = defaultPos.x;
    if (defaultPos.y > baseMaxY) baseMaxY = defaultPos.y;
  });

  const needsCollapse = hasDuplicateCoords || excludedLockerIds.length > 0;

  if (needsCollapse) {
    const byRow = new Map();
    visiblePositions.forEach(p => {
      if (!byRow.has(p.origY)) byRow.set(p.origY, []);
      byRow.get(p.origY).push(p);
    });

    const sortedYs = Array.from(byRow.keys()).sort((a, b) => a - b);
    sortedYs.forEach((origY, newY) => {
      const list = byRow.get(origY);
      // 빈 행만 위로 collapse — 행 내 X 위치는 신규 락커와의 좌표 일관성을 위해 유지 //
      // (hasDuplicateCoords 의 legacy 데이터는 defaultPos가 (i%7, i/7)로 이미 unique 하므로 영향 없음) //
      list.forEach((p) => {
        lockerMap[`${p.origX},${newY}`] = { locker: p.locker, arrayIdx: p.arrayIdx };
        if (p.origX > maxX) maxX = p.origX;
        if (newY > maxY) maxY = newY;
      });
    });
  }
  else {
    visiblePositions.forEach(p => {
      lockerMap[`${p.origX},${p.origY}`] = { locker: p.locker, arrayIdx: p.arrayIdx };
      if (p.origX > maxX) maxX = p.origX;
      if (p.origY > maxY) maxY = p.origY;
    });
  }

  // collapse가 적용된 경우에는 collapse 결과 maxX/Y를, 아닌 경우 원래 좌표 기준 baseMaxX/Y를 사용 //
  const baseCols = visibleLockers.length > 0 ? (needsCollapse ? maxX : baseMaxX) + 1 : 0;
  const baseRows = visibleLockers.length > 0 ? (needsCollapse ? maxY : baseMaxY) + 1 : 0;

  // newLockerPosMap null일 때 (행/열 추가 직후) base + added 로 영역 산출 //
  // non-null일 때는 실제 신규 락커 좌표 기준으로 effective 산출 — 행/열 삭제 후 잔존 빈 영역 제거 //
  let effectiveCols;
  let effectiveRows;

  if (newLockerPosMap === null) {
    effectiveCols = baseCols + addedCols;
    effectiveRows = baseRows + addedRows;
  }
  else {
    let maxXpos = baseCols - 1;
    let maxYpos = baseRows - 1;

    for (const pos of Object.values(newLockerPosMap)) {
      if (!pos) continue;
      if (pos.x > maxXpos) maxXpos = pos.x;
      if (pos.y > maxYpos) maxYpos = pos.y;
    }
    effectiveCols = Math.max(0, maxXpos + 1);
    effectiveRows = Math.max(0, maxYpos + 1);
  }

  // 가로 스크롤이 가능할 때 마우스 휠을 가로 스크롤로 전환 //
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    function onWheel(e) {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [effectiveCols]);

  const actualNewCellMap = {};
  if (newLockerPosMap === null) {
    let localIdx = 0;
    const captured = {};

    for (let r = 0; r < effectiveRows; r++) {
      for (let c = 0; c < effectiveCols; c++) {
        if (!lockerMap[`${c},${r}`] && (c >= baseCols || r >= baseRows)) {
          actualNewCellMap[`${c},${r}`] = localIdx;
          captured[localIdx] = { x: c, y: r };
          localIdx++;
        }
      }
    }

    newLockerInitialPositions.current = captured;
  }
  else {
    for (const [localIdxStr, pos] of Object.entries(newLockerPosMap)) {
      if (pos) actualNewCellMap[`${pos.x},${pos.y}`] = Number(localIdxStr);
    }
  }

  if (effectiveCols === 0 || effectiveRows === 0) {
    return (
      <OuterWrapper>
        <BodyWrapper ref={bodyRef}>
          <EmptyEditAddButton onClick={() => {
            setAddedCols(1);
            setAddedRows(1);
            setNewLockerPosMap(null);
            onAddLockersChange?.(divisionIndex, {
              positions: [{ x: 0, y: 0, initialX: 0, initialY: 0, lockerIdx: 0 }],
              baseCount: 0
            });
          }}>
            <PlusIcon />
          </EmptyEditAddButton>
        </BodyWrapper>
        <CustomScrollbar direction='horizontal' scrollRef={bodyRef} showTrackAlways={false} />
      </OuterWrapper>
    );
  }

  const gridCells = [];
  for (let r = 0; r < effectiveRows; r++) {
    for (let c = 0; c < effectiveCols; c++) {
      const entry = lockerMap[`${c},${r}`];
      const newLocalIdx = actualNewCellMap[`${c},${r}`];
      if (entry) {
        gridCells.push({ type: 'existing', locker: entry.locker, arrayIdx: entry.arrayIdx, x: c, y: r });
      }
      else if (newLocalIdx !== undefined) {
        gridCells.push({ type: 'new', x: c, y: r, lockerIdx: data.lockers.length + newLocalIdx });
      }
      else {
        gridCells.push({ type: 'gap', x: c, y: r });
      }
    }
  }

  const colLabels = Array.from({ length: effectiveCols }, (_, i) => String.fromCharCode(65 + i));
  const rowLabels = Array.from({ length: effectiveRows }, (_, i) => i + 1);

  function notifyAddedChange(newAddedCols, newAddedRows) {
    const eCols = baseCols + newAddedCols;
    const eRows = baseRows + newAddedRows;
    const positions = [];
    let idx = 0;
    for (let r = 0; r < eRows; r++) {
      for (let c = 0; c < eCols; c++) {
        if (!lockerMap[`${c},${r}`] && (c >= baseCols || r >= baseRows)) {
          // 신규 추가 시점에는 현재 좌표 = 최초 좌표 //
          positions.push({ x: c, y: r, initialX: c, initialY: r, lockerIdx: data.lockers.length + idx++ });
        }
      }
    }
    onAddLockersChange?.(divisionIndex, { positions, baseCount: data.lockers.length });
  }

  // 현재 actualNewCellMap → newLockerPosMap 형태로 캡처 (null path에서 사용) //
  function captureCurrentNewLockerPositions() {
    const positions = {};
    for (const [posKey, localIdx] of Object.entries(actualNewCellMap)) {
      const [x, y] = posKey.split(',').map(Number);
      positions[localIdx] = { x, y };
    }
    return positions;
  }

  // 현재 effective 크기 계산 (position-based) //
  function computeCurrentEffectiveSize() {
    let mX = baseCols - 1;
    let mY = baseRows - 1;
    const source = newLockerPosMap !== null ? newLockerPosMap : captureCurrentNewLockerPositions();
    for (const pos of Object.values(source)) {
      if (!pos) continue;
      if (pos.x > mX) mX = pos.x;
      if (pos.y > mY) mY = pos.y;
    }
    return { cols: Math.max(0, mX + 1), rows: Math.max(0, mY + 1) };
  }

  // 신규 락커 추가 후 부모 콜백 호출 //
  function emitAddedChange(updatedPosMap) {
    const positions = Object.entries(updatedPosMap).map(([localIdxStr, pos]) => {
      const localIdx = Number(localIdxStr);
      const initial = newLockerInitialPositions.current[localIdx] ?? { x: pos.x, y: pos.y };
      return {
        x: pos.x,
        y: pos.y,
        initialX: initial.x,
        initialY: initial.y,
        lockerIdx: data.lockers.length + localIdx
      };
    });
    onAddLockersChange?.(divisionIndex, { positions, baseCount: data.lockers.length });
  }

  function onClickAddColumn() {
    // 기존 newLockerPosMap 유지 + 새 열의 셀들만 추가 (swap-induced 위치 보존) //
    const updatedPosMap = newLockerPosMap === null ? captureCurrentNewLockerPositions() : { ...newLockerPosMap };
    const { cols: curCols, rows: curRows } = computeCurrentEffectiveSize();
    const newX = curCols;

    let nextLocalIdx;

    if (Object.keys(updatedPosMap).length > 0) {
      nextLocalIdx = Math.max(...Object.keys(updatedPosMap).map(Number)) + 1;
    }
    else {
      nextLocalIdx = 0;
    }

    for (let r = 0; r < curRows; r++) {
      const key = `${newX},${r}`;
      if (lockerMap[key]) continue;
      updatedPosMap[nextLocalIdx] = { x: newX, y: r };
      newLockerInitialPositions.current[nextLocalIdx] = { x: newX, y: r };
      nextLocalIdx++;
    }

    setAddedCols(addedCols + 1);
    setNewLockerPosMap(updatedPosMap);
    emitAddedChange(updatedPosMap);
  }

  function onClickAddRow() {
    const updatedPosMap = newLockerPosMap === null ? captureCurrentNewLockerPositions() : { ...newLockerPosMap };
    const { cols: curCols, rows: curRows } = computeCurrentEffectiveSize();
    const newY = curRows;

    let nextLocalIdx;

    if (Object.keys(updatedPosMap).length > 0) {
      nextLocalIdx = Math.max(...Object.keys(updatedPosMap).map(Number)) + 1;
    }
    else {
      nextLocalIdx = 0;
    }

    for (let c = 0; c < curCols; c++) {
      const key = `${c},${newY}`;
      if (lockerMap[key]) continue;
      updatedPosMap[nextLocalIdx] = { x: c, y: newY };
      newLockerInitialPositions.current[nextLocalIdx] = { x: c, y: newY };
      nextLocalIdx++;
    }

    setAddedRows(addedRows + 1);
    setNewLockerPosMap(updatedPosMap);
    emitAddedChange(updatedPosMap);
  }

  function hasActiveLockerInLine(getEntry, lineLength) {
    for (let i = 0; i < lineLength; i++) {
      const entry = getEntry(i);
      if (!entry || !entry.locker.id) continue;
      if (entry.locker.state === 'Using' || entry.locker.state === 'Expected') return true;
    }
    return false;
  }

  // 행/열 삭제 후 부모 콜백 호출 (signature 유지) //
  // 호출 순서 주의: onDeleteLockersFn이 부모의 swap/add ref를 리셋하므로 먼저 호출해야 //
  // 그 뒤에 onSwapChange/onAddLockersChange로 surgical update 결과를 넣어야 함 //
  function emitDeletionUpdates(newSwapMap, newPosMap, idsToDelete) {
    if (idsToDelete.length > 0) onDeleteLockersFn?.(divisionIndex, idsToDelete);
    onSwapChange?.(divisionIndex, newSwapMap);
    const positions = Object.entries(newPosMap).map(([localIdxStr, pos]) => {
      const localIdx = Number(localIdxStr);
      const initial = newLockerInitialPositions.current[localIdx] ?? { x: pos.x, y: pos.y };
      return {
        x: pos.x,
        y: pos.y,
        initialX: initial.x,
        initialY: initial.y,
        lockerIdx: data.lockers.length + localIdx
      };
    });
    onAddLockersChange?.(divisionIndex, { positions, baseCount: data.lockers.length });
  }

  function onClickDeleteColumn(colIndex) {
    if (hasActiveLockerInLine(r => lockerMap[`${colIndex},${r}`], effectiveRows)) {
      alert(t('main.locker.cannotDeleteActiveLine'));
      return;
    }

    // 삭제 대상 수집: 시각적으로 colIndex에 있는 기존 + 신규 락커 //
    const idsToDelete = [];
    const newLocalIdxsToRemove = new Set();
    for (let r = 0; r < effectiveRows; r++) {
      const entry = lockerMap[`${colIndex},${r}`];
      if (entry && entry.locker.id) idsToDelete.push(entry.locker.id);
      const newLocalIdx = actualNewCellMap[`${colIndex},${r}`];
      if (newLocalIdx !== undefined) newLocalIdxsToRemove.add(newLocalIdx);
    }
    const deletedIdSet = new Set(idsToDelete);

    // 기존 락커: 삭제 제외, x > colIndex 인 락커들은 왼쪽으로 한 칸 shift //
    const newSwapMap = {};
    data.lockers.forEach(l => {
      if (excludedIdSet.has(l.id) || deletedIdSet.has(l.id)) return;
      const defaultPos = { x: l.x ?? 0, y: l.y ?? 0 };
      const currentPos = swapMap[l.id] ?? defaultPos;
      if (currentPos.x > colIndex) {
        newSwapMap[l.id] = { x: currentPos.x - 1, y: currentPos.y };
      }
      else if (swapMap[l.id]) {
        newSwapMap[l.id] = currentPos;
      }
    });

    // 신규 락커: 삭제 제외, x > colIndex 는 왼쪽으로 한 칸 shift //
    const currentNewPositions = {};
    for (const [posKey, localIdx] of Object.entries(actualNewCellMap)) {
      const [px, py] = posKey.split(',').map(Number);
      currentNewPositions[localIdx] = { x: px, y: py };
    }
    const newLockerPosMapUpdated = {};
    for (const [localIdxStr, pos] of Object.entries(currentNewPositions)) {
      const localIdx = Number(localIdxStr);
      if (newLocalIdxsToRemove.has(localIdx)) continue;
      newLockerPosMapUpdated[localIdx] = pos.x > colIndex
        ? { x: pos.x - 1, y: pos.y }
        : pos;
    }

    setSwapMap(newSwapMap);
    setNewLockerPosMap(newLockerPosMapUpdated);
    if (colIndex >= baseCols) setAddedCols(prev => Math.max(0, prev - 1));

    emitDeletionUpdates(newSwapMap, newLockerPosMapUpdated, idsToDelete);
  }

  function onClickDeleteRow(rowIndex) {
    if (hasActiveLockerInLine(c => lockerMap[`${c},${rowIndex}`], effectiveCols)) {
      alert(t('main.locker.cannotDeleteActiveLine'));
      return;
    }

    // 삭제 대상 수집: 시각적으로 rowIndex에 있는 기존 + 신규 락커 //
    const idsToDelete = [];
    const newLocalIdxsToRemove = new Set();
    for (let c = 0; c < effectiveCols; c++) {
      const entry = lockerMap[`${c},${rowIndex}`];
      if (entry && entry.locker.id) idsToDelete.push(entry.locker.id);
      const newLocalIdx = actualNewCellMap[`${c},${rowIndex}`];
      if (newLocalIdx !== undefined) newLocalIdxsToRemove.add(newLocalIdx);
    }
    const deletedIdSet = new Set(idsToDelete);

    // 기존 락커: 삭제 제외, y > rowIndex 인 락커들은 위로 한 칸 shift //
    const newSwapMap = {};
    data.lockers.forEach(l => {
      if (excludedIdSet.has(l.id) || deletedIdSet.has(l.id)) return;
      const defaultPos = { x: l.x ?? 0, y: l.y ?? 0 };
      const currentPos = swapMap[l.id] ?? defaultPos;
      if (currentPos.y > rowIndex) {
        newSwapMap[l.id] = { x: currentPos.x, y: currentPos.y - 1 };
      }
      else if (swapMap[l.id]) {
        newSwapMap[l.id] = currentPos;
      }
    });

    // 신규 락커: 삭제 제외, y > rowIndex 는 위로 한 칸 shift //
    const currentNewPositions = {};
    for (const [posKey, localIdx] of Object.entries(actualNewCellMap)) {
      const [px, py] = posKey.split(',').map(Number);
      currentNewPositions[localIdx] = { x: px, y: py };
    }
    const newLockerPosMapUpdated = {};
    for (const [localIdxStr, pos] of Object.entries(currentNewPositions)) {
      const localIdx = Number(localIdxStr);
      if (newLocalIdxsToRemove.has(localIdx)) continue;
      newLockerPosMapUpdated[localIdx] = pos.y > rowIndex
        ? { x: pos.x, y: pos.y - 1 }
        : pos;
    }

    setSwapMap(newSwapMap);
    setNewLockerPosMap(newLockerPosMapUpdated);
    if (rowIndex >= baseRows) setAddedRows(prev => Math.max(0, prev - 1));

    emitDeletionUpdates(newSwapMap, newLockerPosMapUpdated, idsToDelete);
  }

  function onDropLockerCell(targetX, targetY) {
    if (dragSourceKey === null) return;
    const [srcX, srcY] = dragSourceKey.split(',').map(Number);
    if (srcX === targetX && srcY === targetY) { setDragSourceKey(null); return; }

    const srcExisting = lockerMap[`${srcX},${srcY}`];
    const tgtExisting = lockerMap[`${targetX},${targetY}`];
    const srcNewLocalIdx = actualNewCellMap[`${srcX},${srcY}`];
    const tgtNewLocalIdx = actualNewCellMap[`${targetX},${targetY}`];

    if (!srcExisting && srcNewLocalIdx === undefined) { setDragSourceKey(null); return; }
    if (!tgtExisting && tgtNewLocalIdx === undefined) { setDragSourceKey(null); return; }

    const newSwapMap = { ...swapMap };

    const currentNewLockerPosMap = {};
    for (const [posKey, localIdx] of Object.entries(actualNewCellMap)) {
      const [px, py] = posKey.split(',').map(Number);
      currentNewLockerPosMap[localIdx] = { x: px, y: py };
    }
    const newNewLockerPosMap = { ...currentNewLockerPosMap };

    if (srcExisting && tgtExisting) {
      newSwapMap[srcExisting.locker.id] = { x: targetX, y: targetY };
      newSwapMap[tgtExisting.locker.id] = { x: srcX, y: srcY };
      setSwapMap(newSwapMap);
      onSwapChange?.(divisionIndex, newSwapMap);
    }
    else {
      if (srcNewLocalIdx !== undefined && tgtNewLocalIdx !== undefined) {
        newNewLockerPosMap[srcNewLocalIdx] = { x: targetX, y: targetY };
        newNewLockerPosMap[tgtNewLocalIdx] = { x: srcX, y: srcY };
      }
      else if (srcExisting && tgtNewLocalIdx !== undefined) {
        newSwapMap[srcExisting.locker.id] = { x: targetX, y: targetY };
        newNewLockerPosMap[tgtNewLocalIdx] = { x: srcX, y: srcY };
      }
      else {
        newNewLockerPosMap[srcNewLocalIdx] = { x: targetX, y: targetY };
        newSwapMap[tgtExisting.locker.id] = { x: srcX, y: srcY };
      }
      setSwapMap(newSwapMap);
      setNewLockerPosMap(newNewLockerPosMap);
      onSwapChange?.(divisionIndex, newSwapMap);
      const positions = Object.entries(newNewLockerPosMap).map(([localIdxStr, pos]) => {
        const localIdx = Number(localIdxStr);
        const initial = newLockerInitialPositions.current[localIdx] ?? { x: pos.x, y: pos.y };

        return {
          x: pos.x,
          y: pos.y,
          initialX: initial.x,
          initialY: initial.y,
          lockerIdx: data.lockers.length + localIdx
        };
      });
      onAddLockersChange?.(divisionIndex, { positions, baseCount: data.lockers.length });
    }

    setDragSourceKey(null);
  }

  function handleDragStart(event) {
    setDragSourceKey(event.active.id);
  }

  function handleDragOver(event) {
    const { over, active } = event;
    if (!over || over.id === active.id) {
      setDragOverKey(null);
      return;
    }
    setDragOverKey(over.id);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setDragSourceKey(null);
    setDragOverKey(null);
    if (!over || active.id === over.id) return;
    const [x, y] = over.id.split(',').map(Number);
    onDropLockerCell(x, y);
  }

  function handleDragCancel() {
    setDragSourceKey(null);
    setDragOverKey(null);
  }

  // 드래그 오버레이 렌더링용 활성 셀 데이터 //
  function getOverlayCellData() {
    if (!dragSourceKey) return null;
    const [sx, sy] = dragSourceKey.split(',').map(Number);
    const existingNames = new Set(visibleLockers.map(l => l.lockerName).filter(Boolean));
    const cell = gridCells.find(c => c.x === sx && c.y === sy && c.type !== 'gap');
    if (!cell) return null;
    if (cell.type === 'existing') return cell.locker;
    const localIdx = cell.lockerIdx - data.lockers.length;
    const initialPos = newLockerInitialPositions.current[localIdx] ?? { x: cell.x, y: cell.y };
    const base = `${divisionNameDraft ?? data.name} ${String.fromCharCode(65 + initialPos.x)}-${initialPos.y + 1}`;
    let name = base;
    if (existingNames.has(base)) {
      let suffix = 2;
      while (existingNames.has(`${base}(${suffix})`)) suffix++;
      name = `${base}(${suffix})`;
    }
    return { state: 'Empty', lockerName: name, _isNew: true };
  }

  const overlayCellData = getOverlayCellData();

  // 락커 그리드 셀 렌더링 (existing / new / gap 분기) //
  function renderGridCells() {
    const existingNames = new Set(visibleLockers.map(l => l.lockerName).filter(Boolean));

    const buildUniqueAutoName = (x, y) => {
      const base = `${divisionNameDraft ?? data.name} ${String.fromCharCode(65 + x)}-${y + 1}`;
      if (!existingNames.has(base)) return base;
      let suffix = 2;
      while (existingNames.has(`${base}(${suffix})`)) suffix++;
      return `${base}(${suffix})`;
    };

    return gridCells.map((cell) => {
      if (cell.type === 'gap') return <LockerGapCell key={`gap-${cell.x}-${cell.y}`} />;

      let lockerData;
      if (cell.type === 'existing') {
        lockerData = cell.locker;
      }
      else {
        // 신규 락커 이름은 최초 좌표 기준 (swap 후에도 고정) //
        const localIdx = cell.lockerIdx - data.lockers.length;
        const initialPos = newLockerInitialPositions.current[localIdx] ?? { x: cell.x, y: cell.y };
        lockerData = { state: 'Empty', lockerName: buildUniqueAutoName(initialPos.x, initialPos.y), _isNew: true };
      }

      const lockerIdx = cell.type === 'existing' ? cell.arrayIdx : cell.lockerIdx;
      const cellKey = `${cell.x},${cell.y}`;
      const isDraggable = cell.type === 'existing' || cell.type === 'new';

      return (
        <DraggableLockerCell
          key={cell.type === 'existing' ? `Locker-${cell.locker.id}` : `Locker-new-${cell.x}-${cell.y}`}
          cellKey={cellKey}
          isDraggable={isDraggable}
          isDragOver={isDraggable && dragOverKey === cellKey}>
          <LockerInfoEdit
            data={lockerData}
            index={divisionIndex}
            lockerIndex={lockerIdx}
            ref={element => {
              if (!LockerRef.current[divisionIndex]) {
                LockerRef.current[divisionIndex] = [];
              }
              LockerRef.current[divisionIndex][lockerIdx] = element;
            }}
            onLockChange={(lockerIndex, hasChanged, newIsLocked) => onLockChange?.(lockerIndex, hasChanged, newIsLocked)}
            onNameChange={(lockerIndex, hasChanged, newName) => onNameChange?.(lockerIndex, hasChanged, newName)}
            onClickUsageHistoryFn={onClickUsageHistoryFn}
            onClickLockerActionFn={onClickLockerActionFn}
            onClickChangeStateFn={onClickChangeStateFn}
            onClickDeleteLockerFn={onClickDeleteLockerFn}
            onClickChangeNameFn={onClickChangeNameFn}
            onClickSendMessageFn={onClickSendMessageFn}
            onClickExtendFn={onClickExtendFn} />
        </DraggableLockerCell>
      );
    });
  }

  return (
    <OuterWrapper>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}>
    <BodyWrapper ref={bodyRef}>
      <NumberColumn>
        {
          rowLabels.map(num => (
            <NumberCell key={'row-label-' + num}>
              <NumberLabelWrapper onClick={() => onClickDeleteRow(num - 1)}>
                <NumberDeleteBackground $colCount={colLabels.length} />
                <RowText>{num}</RowText>
                <DeleteRowButton>
                  <DeleteRowIcon />
                </DeleteRowButton>
              </NumberLabelWrapper>
            </NumberCell>
          ))
        }
      </NumberColumn>
      <ContentColumn>
        <AlphabetRow>
          {
            colLabels.map((label, i) => (
              <AlphabetCell key={'col-label-' + label}>
                <AlphabetLabelWrapper onClick={() => onClickDeleteColumn(i)}>
                  <AlphabetDeleteBackground $rowCount={effectiveRows} />
                  <ColText>{label}</ColText>
                  <DeleteColumnButton>
                    <DeleteColumnIcon />
                  </DeleteColumnButton>
                </AlphabetLabelWrapper>
              </AlphabetCell>
            ))
          }
        </AlphabetRow>
        <InfoGridWrapper>
          <InfoBox $cols={effectiveCols}>
            {renderGridCells()}
          </InfoBox>
          <RightPlusButton onClick={onClickAddColumn}>
            <PlusIcon />
          </RightPlusButton>
        </InfoGridWrapper>
        <BottomPlusButton onClick={onClickAddRow}>
          <PlusIcon />
        </BottomPlusButton>
      </ContentColumn>
    </BodyWrapper>
    <DragOverlay dropAnimation={null}>
      {
        overlayCellData &&
        <LockerInfoEdit
          data={overlayCellData}
          index={divisionIndex}
          lockerIndex={-1}
          onLockChange={() => {}}
          onNameChange={() => {}} />
      }
    </DragOverlay>
    </DndContext>
    <CustomScrollbarWrapper>
      {
        effectiveCols >= 7 &&
        <CustomScrollbar direction='horizontal' scrollRef={bodyRef} isColorPrimary={true} isTrackThin={true} showTrackAlways={false} />
      }
    </CustomScrollbarWrapper>
    </OuterWrapper>
  );
}


export default LockerEditView;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: row;

  gap: 20px;

  width: 100%;
  box-sizing: border-box;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NumberColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;

  gap: 15px;

  padding-top: 38.5px;

  flex-shrink: 0;
`;

const NumberCell = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 38px;
  height: 122px;

  flex-shrink: 0;
`;

const NumberDeleteBackground = styled.div`
  position: absolute;
  top: -7.5px;
  left: 0px;

  width: ${props => props.$colCount * 122 + (props.$colCount - 1) * 20 + 69}px;
  height: 139px;

  border-radius: 8px;
  background: linear-gradient(
    to right,
    ${props => props.theme.pointRed},
    ${props => props.theme.pointRedAlpha20}
  );

  pointer-events: none;
  opacity: 0;
  transition: all 0.3s ease-in-out;
`;

const RowText = styled.span`
  position: relative;
  z-index: 1;

  margin-bottom: -18px;
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.subText};

  transition: all 0.3s ease-in-out;
`;

const DeleteRowButton = styled.div`
  position: relative;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 0;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DeleteRowIcon = styled(DeleteSvg)`
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  color: ${props => props.theme.text};
`;

const NumberLabelWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 38px;
  height: 122px;
  
  gap: 10px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }

    &:hover ${NumberDeleteBackground} {
      opacity: 1;
    }

    &:hover ${RowText} {
      margin-bottom: 0px;
      color: ${props => props.theme.text};
    }

    &:hover ${DeleteRowButton} {
      opacity: 1;
    }
  }
`;

const ContentColumn = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  gap: 15px;
`;

const AlphabetRow = styled.div`
  display: flex;
  flex-direction: row;

  padding: 6px 0 0 0;

  gap: 20px;
`;

const AlphabetDeleteBackground = styled.div`
  position: absolute;
  top: -6px;
  left: -7.5px;

  width: 139px;
  height: ${props => props.$rowCount * 122 + (props.$rowCount - 1) * 15 + 50}px;

  border-radius: 8px;
  background: linear-gradient(
    to bottom,
    ${props => props.theme.pointRed},
    ${props => props.theme.pointRedAlpha20}
  );

  pointer-events: none;
  opacity: 0;
  transition: all 0.3s ease-in-out;
`;

const ColText = styled.span`
  position: relative;
  z-index: 1;

  margin-right: -29px;
  color: ${props => props.theme.subText};

  transition: all 0.3s ease-in-out;
`;

const DeleteColumnButton = styled.div`
  position: relative;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 0;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DeleteColumnIcon = styled(DeleteSvg)`
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  color: ${props => props.theme.text};
`;

const AlphabetLabelWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 122px;
  height: 18px;

  gap: 10px;

  font-family: NotoSansKR-Medium;
  font-size: 15px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }

    &:hover ${AlphabetDeleteBackground} {
      opacity: 1;
    }

    &:hover ${ColText} {
      margin-right: 0px;
      color: ${props => props.theme.text};
    }

    &:hover ${DeleteColumnButton} {
      opacity: 1;
    }
  }
`;

const AlphabetCell = styled.div`
  flex-shrink: 0;
`;

const InfoGridWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  gap: 20px;
`;

const InfoBox = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols}, 122px);

  gap: 15px 20px;
`;

const RightPlusButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  margin: 30px 0 30px 0;

  width: 30px;
  flex-shrink: 0;

  border-radius: 20px;
  background-color: ${props => props.theme.primaryAlpha20};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
    }
  }
`;

const BottomPlusButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  margin: 5px 0 35px 30px;

  width: calc(100% - 110px);
  height: 30px;

  flex-shrink: 0;

  border-radius: 20px;
  background-color: ${props => props.theme.primaryAlpha20};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
    }
  }
`;

const PlusIcon = styled(PlusSvg)`
  width: 20px;
  height: 20px;

  flex-shrink: 0;

  color: ${props => props.theme.subText};
`;

const EmptyEditAddButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  align-self: flex-start;

  width: 122px;
  height: 122px;

  border-radius: 12px;
  background-color: ${props => props.theme.primaryAlpha10};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primaryAlpha20};
    }
  }
`;

const LockerGapCell = styled.div`
  width: 122px;
  height: 122px;
  flex-shrink: 0;
`;

const DragWrapper = styled.div`
  width: 122px;
  height: 122px;
  flex-shrink: 0;

  opacity: ${props => props.$isDragging ? 0.4 : 1};
  outline: 2px solid ${props => props.$isDragOver ? props.theme.primary : 'transparent'};
  border-radius: 12px;

  cursor: ${props => props.$isDraggable ? 'grab' : 'default'};

  transition: opacity 0.15s ease, outline-color 0.15s ease;
`;

const CustomScrollbarWrapper = styled.div`
  padding: 0 0 0 60px;

  /* CustomScrollbar Track height(1px) + margin-top(10px) = 11px 만큼 고정 — 스크롤바 표시 여부와 무관하게 레이아웃 점프 방지 */
  height: 11px;
  box-sizing: content-box;
`;
